import React from "react";
import { AtSign, Facebook, Instagram, Twitter } from "lucide-react";
import seoConfig from "@/lib/seo-pages.json";

/**
 * Travis's own public profiles.
 *
 * TRUTHFULNESS CONTRACT
 *  * These are the founder's PERSONAL accounts, supplied by Travis himself.
 *    They are labelled "Travis on ..." and never "Sponsor Me on ...", because
 *    Sponsor Me has no official business social accounts yet.
 *  * The URLs live in src/lib/seo-pages.json (organization.founderLinks) so the
 *    visible links and the founder Person `sameAs` in the JSON-LD graph come
 *    from one place and cannot drift. They are attached to Person.sameAs only -
 *    never Organization.sameAs.
 *  * Nothing is invented. If a network is not in that list (LinkedIn, for
 *    example) no link is rendered for it.
 *  * External links open in a new tab with rel="me noopener noreferrer": "me"
 *    states the identity relationship, the rest is standard safe-external
 *    handling.
 */
export const FOUNDER_LINKS = seoConfig.organization.founderLinks || [];

const ICONS = {
  facebook: Facebook,
  // lucide-react ships the bird mark rather than an X glyph; the visible label
  // and aria-label both say "X", so the wording is correct even though the icon
  // is the legacy one.
  x: Twitter,
  instagram: Instagram,
  // No Threads glyph exists in lucide-react - the @ mark is the closest honest
  // stand-in and the label carries the meaning.
  threads: AtSign,
};

export default function FounderSocialLinks({
  variant = "card",
  testIdPrefix = "founder-social",
  className = "",
}) {
  if (!FOUNDER_LINKS.length) return null;

  const isCard = variant === "card";

  return (
    <ul
      data-testid={`${testIdPrefix}-links`}
      className={`flex flex-wrap gap-2 ${className}`}
    >
      {FOUNDER_LINKS.map((link) => {
        const Icon = ICONS[link.network] || AtSign;
        return (
          <li key={link.href}>
            <a
              href={link.href}
              target="_blank"
              rel="me noopener noreferrer"
              aria-label={link.label}
              data-testid={`${testIdPrefix}-${link.network}`}
              className={
                isCard
                  ? "inline-flex items-center gap-2 min-h-[44px] rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#1F6F78] transition-colors duration-150 hover:bg-[#E9F3F3] active:bg-[#D7EEF0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78]"
                  : "inline-flex items-center gap-2 min-h-[44px] px-1 py-2.5 text-sm text-[#4B5A60] transition-colors duration-150 hover:text-[#1F6F78] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78]"
              }
            >
              <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
              {link.label}
            </a>
          </li>
        );
      })}
    </ul>
  );
}
